﻿import { Ionicons } from '@expo/vector-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dimensions,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

const { width: SCREEN_W } = Dimensions.get('window');
const GOLD = '#C9A843';
const CARD_BG = '#1A2B3C';

type Slide = {
  icon: keyof typeof Ionicons.glyphMap;
  iconColor: string;
  title: string;
  body: string;
  mock?: string;
};

type Props = {
  visible: boolean;
  onDone: () => void;
};

export function PortfolioTutorial({ visible, onDone }: Props) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const insets = useSafeAreaInsets();
  const [index, setIndex] = useState(0);

  const SLIDES: Slide[] = useMemo(() => [
    { icon: 'briefcase-outline', iconColor: GOLD, title: t('portfolioTutorial.slide1_title'), body: t('portfolioTutorial.slide1_body') },
    { icon: 'create-outline', iconColor: '#1E3A8A', title: t('portfolioTutorial.slide2_title'), body: t('portfolioTutorial.slide2_body'), mock: t('portfolioTutorial.slide2_mock') },
    { icon: 'share-outline', iconColor: '#059669', title: t('portfolioTutorial.slide3_title'), body: t('portfolioTutorial.slide3_body') },
    { icon: 'person-outline', iconColor: '#A78BFA', title: t('portfolioTutorial.slide4_title'), body: t('portfolioTutorial.slide4_body') },
    { icon: 'checkmark-circle-outline', iconColor: '#059669', title: t('portfolioTutorial.slide5_title'), body: t('portfolioTutorial.slide5_body') },
  ], [t]);


  function goNext() {
    if (index < SLIDES.length - 1) {
      setIndex(index + 1);
    } else {
      onDone();
    }
  }

  function goSkip() {
    onDone();
  }

  const slide = SLIDES[index];

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={goSkip}>
      <View style={[styles.container, { paddingTop: insets.top + spacing.md, paddingBottom: insets.bottom + spacing.lg }]}>

        {/* Header */}
        <View style={styles.header}>
          <Pressable onPress={goSkip} hitSlop={12} accessibilityRole="button">
            <Text style={styles.skip}>{t('portfolioTutorial.skip')}</Text>
          </Pressable>
          <Text style={styles.counter}>{index + 1} / {SLIDES.length}</Text>
        </View>

        {/* Slide */}
        <View style={styles.list}>
          <View style={styles.slide}>
            <View style={[styles.iconCircle, { borderColor: slide.iconColor + '44' }]}>
              <Ionicons name={slide.icon} size={48} color={slide.iconColor} />
            </View>
            <Text style={styles.slideTitle}>{slide.title}</Text>
            <Text style={styles.slideBody}>{slide.body}</Text>
            {!!slide.mock && (
              <View style={styles.mockBox}>
                <Text style={styles.mockText}>{slide.mock}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Dots */}
        <View style={styles.dots}>
          {SLIDES.map((_, i) => (
            <View
              key={i}
              style={[styles.dot, i === index && styles.dotActive]}
            />
          ))}
        </View>

        {/* CTA */}
        <Pressable
          style={({ pressed }) => [styles.nextBtn, pressed && styles.nextBtnPressed]}
          onPress={goNext}
          accessibilityRole="button"
        >
          <Text style={styles.nextLabel}>
            {index === SLIDES.length - 1 ? t('portfolioTutorial.start') : t('portfolioTutorial.next')}
          </Text>
        </Pressable>
      </View>
    </Modal>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    paddingHorizontal: spacing.lg,
    gap: spacing.lg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  skip: { color: colors.textMuted, fontSize: fontSize.md },
  counter: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
  list: { flexGrow: 0 },
  slide: {
    width: SCREEN_W - spacing.lg * 2,
    gap: spacing.md,
    alignItems: 'center',
    paddingVertical: spacing.sm,
  },
  iconCircle: {
    width: 96,
    height: 96,
    borderRadius: 48,
    borderWidth: 2,
    backgroundColor: CARD_BG,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
  },
  slideTitle: {
    color: colors.text,
    fontSize: fontSize.xl,
    fontWeight: '700',
    textAlign: 'center',
  },
  slideBody: {
    color: colors.textMuted,
    fontSize: fontSize.md,
    lineHeight: 24,
    textAlign: 'left',
    width: '100%',
  },
  mockBox: {
    width: '100%',
    backgroundColor: CARD_BG,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: 'rgba(201,168,67,0.25)',
    padding: spacing.md,
  },
  mockText: {
    color: GOLD,
    fontSize: fontSize.sm,
    lineHeight: 22,
    fontFamily: Platform.OS === 'ios' ? 'Courier New' : 'monospace',
  },
  dots: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.border,
  },
  dotActive: {
    backgroundColor: GOLD,
    width: 20,
  },
  nextBtn: {
    height: 52,
    borderRadius: radius.md,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nextBtnPressed: { opacity: 0.85 },
  nextLabel: {
    color: colors.textOnPrimary,
    fontSize: fontSize.md,
    fontWeight: '700',
  },
  });
}

